import { AnyAction, Dispatch } from 'redux';
import { getCompensationWithHandler } from '../../../../../global/ducks/benadmin-api';
import { stringSort } from '../../../../../global/helpers/sort-helper';
import { ApiStatuses } from '../../../../../global/models/api-model';
import { Compensation, CompensationHistoryActions, CompensationHistoryState, OneTimePayment, CompensationResponseData, ScheduledPayment, CalculatedTotals } from './compensation-history-model';

const InitialState: CompensationHistoryState = {
    pre2018: false,
    pre2018ScheduledPayments: [],
    post2018ScheduledPayments: [],
    allScheduledPayments: [],
    oneTimePayments: [],
    allCompensations: [],
    temporaryCalculatedTotals: null,
    compensationLoaded: false,
    loadingCompensation: 0,
    updateCompensationStatus: ApiStatuses.None,
};

const dividingPoint = new Date('2017-12-31');

export const post2018 = (a: string) => {
	return new Date(a) > dividingPoint;
};

const parseCompensations = (loadedCompensation: Compensation[], loadingCompensation: Compensation[]) => {
	const combined = loadedCompensation.concat(loadingCompensation).sort((a, b) => stringSort(b.effective_date, a.effective_date));
	const otp = combined.filter(a => (a as any).otp_type).map(b => b as OneTimePayment);
	const sp = combined.filter(a => !(a as any).otp_type).map(b => b as ScheduledPayment);
	return {
		pre2018: sp.filter(a => !post2018(a.effective_date)),
		post2018: sp.filter(a => post2018(a.effective_date)),
		allScheduledPayments: sp,
		onetime: otp,
		allCompensations: combined,
	};
};

const addScheduledPaymentToArrays = (data: CompensationResponseData,
	allScheduledPayments: ScheduledPayment[]) => {
	const { errors, otp, ...scheduledPayment } = data;

	const updatedScheduledPayments = [...allScheduledPayments.filter(a => a.id.toString() !== scheduledPayment.id.toString()), scheduledPayment];
	updatedScheduledPayments.sort((a, b) => stringSort(b.effective_date, a.effective_date));

	return {
		pre2018: updatedScheduledPayments.filter(a => !post2018(a.effective_date)),
		post2018: updatedScheduledPayments.filter(a => post2018(a.effective_date)),
		allScheduledPayments: updatedScheduledPayments,
	};
};

const addOneTimePaymentToArrays = (data: CompensationResponseData, oneTimePayments: OneTimePayment[]) => {
    const { otp } = data;

    const updatedOneTimePayments = [...oneTimePayments, otp[0]];
    updatedOneTimePayments.sort((a, b) => stringSort(b.effective_date, a.effective_date));

    return updatedOneTimePayments;
};

export const compensationHistoryReducer = (state = InitialState, action: AnyAction): CompensationHistoryState => {

	switch (action.type) {
		case CompensationHistoryActions.ADD_COMPENSATION:
			return { ...state, loadingCompensation: state.loadingCompensation + 1 };

		case CompensationHistoryActions.SET_COMPENSATION_LOADED:
			return { ...state, compensationLoaded: true };

		case CompensationHistoryActions.SET_COMPENSATION: {
			const parsed = parseCompensations(state.allCompensations, action.payload.compensations?.concat(action.payload.otp));
			return {
				...state,
				oneTimePayments: parsed.onetime,
				pre2018ScheduledPayments: parsed.pre2018,
				post2018ScheduledPayments: parsed.post2018,
				allScheduledPayments: parsed.allScheduledPayments,
				allCompensations: parsed.allCompensations,
				loadingCompensation: state.loadingCompensation - 1,
			};
		}
		case CompensationHistoryActions.SET_COMPENSATION_FAIL: {
			return { ...state, loadingCompensation: state.loadingCompensation - 1 };
		}

        case CompensationHistoryActions.TOGGLE_PRE_2018: {
            return { ...state, pre2018: action.payload };
        }
        case CompensationHistoryActions.ADD_SCHEDULED_PAYMENT: {
            const updatedArrays = addScheduledPaymentToArrays(
                action.payload,
                state.allScheduledPayments);
            return {
                ...state,
                allScheduledPayments: updatedArrays.allScheduledPayments,
                post2018ScheduledPayments: updatedArrays.post2018,
                pre2018ScheduledPayments: updatedArrays.pre2018,
            };
        }
        case CompensationHistoryActions.ADD_ONE_TIME_PAYMENT: {    
            return {
                ...state,
                oneTimePayments: addOneTimePaymentToArrays(action.payload, state.oneTimePayments),
            };
        }
        case CompensationHistoryActions.UPDATE_COMPENSATION_STATUS: {
            return {
                ...state,
                updateCompensationStatus: action.payload,
            };
        }
        case CompensationHistoryActions.SET_CALCULATED_TOTALS: {
            return {
                ...state,
                temporaryCalculatedTotals: action.payload,
            };
		}
		case CompensationHistoryActions.UNLOAD_COMPENSATION_STATE: {
			return {
				pre2018: false,
				pre2018ScheduledPayments: [],
				post2018ScheduledPayments: [],
				allScheduledPayments: [],
				oneTimePayments: [],
				allCompensations: [],
				temporaryCalculatedTotals: null,
				compensationLoaded: false,
				loadingCompensation: 0,
				updateCompensationStatus: ApiStatuses.None,
			};
		}
        default: return state;
    }
};

export const addLoadingCompensation = () => {
	return {
		type: CompensationHistoryActions.ADD_COMPENSATION,

	};
};

export const setCompensationLoaded = () => {
	return {
		type: CompensationHistoryActions.SET_COMPENSATION_LOADED,
	};
};


export const setCompensation = (scheduledPayment: any) => {
	return {
		type: CompensationHistoryActions.SET_COMPENSATION,
		payload: scheduledPayment,
	};
};

export const dispatchSetCompensation = (dispatch: Dispatch<any>, loadedAll?: boolean) => (response) => {

	if (loadedAll) {
		dispatch(setCompensationLoaded());
	}

	dispatch(setCompensation(response.data));

};


export const setCompensationFail = () => {
	return {
		type: CompensationHistoryActions.SET_COMPENSATION_FAIL,
	};
};

export const dispatchSetCompensationFail = (dispatch: Dispatch<any>, loadedAll?: boolean) => {

	if (loadedAll) {
		dispatch(setCompensationLoaded());
	}
	dispatch(setCompensationFail());

};


export const togglePre2018 = (toggle: boolean) => {
	return {
		type: CompensationHistoryActions.TOGGLE_PRE_2018,
		payload: toggle,
	};
};

export const dispatchGetCompensations = (dispatch: Dispatch<any>, employmentIds: number[], employeeId: number | string) => {

	for (let i = 0; i < employmentIds.length; i += 1) {
		dispatch(addLoadingCompensation());
        dispatch(getCompensationWithHandler(employeeId, employmentIds[i], dispatchSetCompensation(dispatch, i === employmentIds.length - 1), () => dispatchSetCompensationFail(dispatch, i === employmentIds.length - 1)));
	}
};

export const addScheduledPayment = (responseData: CompensationResponseData) => {
	return {
		type: CompensationHistoryActions.ADD_SCHEDULED_PAYMENT,
		payload: responseData,
	};
};

export const addOneTimePayment = (responseData: CompensationResponseData) => {
    return {
        type: CompensationHistoryActions.ADD_ONE_TIME_PAYMENT,
        payload: responseData,
    };
};

export const setUpdateCompensationStatus = (status: ApiStatuses) => {
    return {
        type: CompensationHistoryActions.UPDATE_COMPENSATION_STATUS,
        payload: status,
    };
};

export const setCalculatedTotals = (calculatedTotals: CalculatedTotals) => {
    return {
        type: CompensationHistoryActions.SET_CALCULATED_TOTALS,
        payload: calculatedTotals,
    };
};

export const unloadCompensationState = () => {
	return {
		type: CompensationHistoryActions.UNLOAD_COMPENSATION_STATE,
	};
};
